import { useEffect, useRef, useState, type CSSProperties, type RefObject } from "react";
import { ListChecks, LockOpen, TrendingUp } from "lucide-react";
import { AWAKEN, MindUnfold } from "@/components/landing/mind-unfold";
import { MIND_OUTCOMES } from "@/components/landing/landing-data";
import {
  AnimatePresence,
  motion,
  useAnimationFrame,
  useMotionValue,
  useSpring,
  useTransform,
} from "framer-motion";

/**
 * Halo path traced around the outer silhouette of the head: from beside the ear, up over
 * the crown and down behind the profile toward the chin. Coordinates are in the image's
 * own 646 × 738 space, so the path stays locked to the artwork at every size.
 */
const HALO_PATH = "M 142 330 C 130 200, 210 58, 335 56 C 460 54, 548 160, 546 290 C 545 380, 530 450, 508 520";
const IMG_W = 646;
/** The moon glides the length of the halo, then rests (out of sight) before the next pass. */
const TRAVEL_MS = 9000;
const REST_MS = 1600;
/** Length of the light trail behind the moon, as a share of the path. */
const TRAIL = 0.16;
/** Where the moon sits when motion is reduced: over the crown. */
const STATIC_AT = 0.4;

const OUTCOME_ICONS = [LockOpen, ListChecks, TrendingUp];
/** How long each outcome stays in the pill before the next one arrives. */
const OUTCOME_MS = 3800;

/** "Thought" sparks that rise from the head and fade (x as % of the arch, delay in s). */
const SPARKS = [
  { x: 52, delay: 0 },
  { x: 60, delay: 1.4 },
  { x: 47, delay: 2.6 },
  { x: 64, delay: 3.7 },
  { x: 56, delay: 4.8 },
];

/**
 * Left-hand visual of "UnfoldWin turns reflection into momentum" (homepage only, decorative).
 *
 * - A faint halo line follows the outer silhouette of the head; a small moon glides along
 *   it with a short light trail, fades out as it slips behind the profile, and returns.
 * - With a desktop mouse the whole arch tilts a few degrees on a spring, and the badge
 *   floats at a different depth.
 * - The inner mind unfolds (MindUnfold): when the visual first comes into view the head
 *   brightens, a core of light ignites, a wave ripples out and a constellation of
 *   connections draws itself; then the halo draws in, the moon starts its orbit, and the
 *   outcome pill springs in. Afterwards the core breathes, ripples
 *   leave it, a signal travels the pathways and a few sparks rise.
 * Everything stops off screen / in a hidden tab, and is static with reduced motion.
 */
export function QuestionsVisual({
  sectionRef,
  active,
  entered,
  reduce,
}: {
  sectionRef: RefObject<HTMLElement | null>;
  active: boolean;
  entered: boolean;
  reduce: boolean;
}) {
  const animate = active && !reduce;

  // ── Pointer tilt (spring-smoothed) ──
  const tiltRef = useRef<HTMLDivElement>(null);
  const px = useMotionValue(0);
  const py = useMotionValue(0);
  const spring = { stiffness: 90, damping: 18, mass: 0.6 };
  const sx = useSpring(px, spring);
  const sy = useSpring(py, spring);
  const rotateY = useTransform(sx, (v) => v * 8);
  const rotateX = useTransform(sy, (v) => v * -6);
  const badgeX = useTransform(sx, (v) => v * 26);
  const badgeY = useTransform(sy, (v) => v * 20);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el || reduce) return;
    const capable = window.matchMedia("(hover: hover) and (pointer: fine) and (min-width: 1024px)");
    // Listen across the whole section, but measure against the visual so the arch leans
    // toward the pointer when it is over or near the image (clamped to ±0.5).
    const clamp = (v: number) => Math.max(-0.5, Math.min(0.5, v));
    const onMove = (e: PointerEvent) => {
      const box = tiltRef.current?.getBoundingClientRect();
      if (!box || !capable.matches || e.pointerType !== "mouse") return;
      px.set(clamp((e.clientX - box.left) / box.width - 0.5));
      py.set(clamp((e.clientY - box.top) / box.height - 0.5));
    };
    const onLeave = () => {
      px.set(0);
      py.set(0);
    };
    el.addEventListener("pointermove", onMove, { passive: true });
    el.addEventListener("pointerleave", onLeave);
    return () => {
      el.removeEventListener("pointermove", onMove);
      el.removeEventListener("pointerleave", onLeave);
    };
  }, [sectionRef, reduce, px, py]);

  // ── Halo orbit ──
  const layerRef = useRef<HTMLDivElement>(null);
  const haloRef = useRef<SVGPathElement>(null);
  const scale = useRef(1);
  const clock = useRef(0);
  const moonX = useMotionValue(0);
  const moonY = useMotionValue(0);
  const moonOpacity = useMotionValue(0);
  const trailDash = useMotionValue("0 10000");
  const trailOffset = useMotionValue(0);
  const trailOpacity = useMotionValue(0);

  /** Put the moon (and its trail) at progress p ∈ [0, 1] along the halo. */
  const place = (p: number, fade: number, withTrail: boolean) => {
    const path = haloRef.current;
    if (!path) return;
    const length = path.getTotalLength();
    const eased = (1 - Math.cos(Math.PI * p)) / 2;
    const at = eased * length;
    const pt = path.getPointAtLength(at);
    moonX.set(pt.x * scale.current);
    moonY.set(pt.y * scale.current);
    moonOpacity.set(0.9 * fade);
    const trail = Math.min(at, TRAIL * length);
    trailDash.set(`${trail} ${length}`);
    trailOffset.set(-(at - trail));
    trailOpacity.set(withTrail ? 0.7 * fade : 0);
  };

  const step = (t: number) => {
    const u = t % (TRAVEL_MS + REST_MS);
    if (u > TRAVEL_MS) {
      moonOpacity.set(0);
      trailOpacity.set(0);
      return;
    }
    const p = u / TRAVEL_MS;
    place(p, Math.min(1, p / 0.08, (1 - p) / 0.08), true);
  };

  useEffect(() => {
    const el = layerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      scale.current = entry.contentRect.width / IMG_W;
      if (reduce) place(STATIC_AT, 1, false);
      else step(clock.current);
    });
    ro.observe(el);
    return () => ro.disconnect();
    // place/step only read refs and motion values
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduce]);

  // The orbit (and the rising sparks) begin once the awakening has played out.
  const [orbiting, setOrbiting] = useState(false);
  useEffect(() => {
    if (!entered || reduce) return;
    const id = window.setTimeout(() => setOrbiting(true), AWAKEN.settled * 1000);
    return () => window.clearTimeout(id);
  }, [entered, reduce]);

  // Outcome pill: cycles through what the AI helps people do, once the awakening is done.
  const [outcome, setOutcome] = useState(0);
  useEffect(() => {
    if (!animate || !orbiting) return;
    const id = window.setInterval(() => setOutcome((o) => (o + 1) % MIND_OUTCOMES.length), OUTCOME_MS);
    return () => window.clearInterval(id);
  }, [animate, orbiting]);

  useAnimationFrame((_, delta) => {
    if (!animate || !orbiting) return;
    clock.current += Math.min(delta, 64);
    step(clock.current);
  });

  return (
    <motion.div ref={tiltRef} className="relative" style={{ rotateX, rotateY, transformPerspective: 1100 }}>
      <div className="lp-arch">
        {/* Image, halo and moon share one layer so they drift together on scroll. */}
        <div ref={layerRef} className="lp-inimg absolute inset-0">
          <motion.img
            src="/images/landing/v6/wireframe-head.webp"
            alt=""
            width={646}
            height={738}
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover object-[50%_30%]"
            initial={reduce ? false : { filter: "brightness(0.6) saturate(0.8)" }}
            animate={entered || reduce ? { filter: "brightness(1) saturate(1)" } : undefined}
            transition={{ duration: 1.8, ease: "easeOut", delay: 0.1 }}
          />
          <svg viewBox="0 0 646 738" className="absolute inset-0 h-full w-full" aria-hidden>
            <MindUnfold awake={entered} animate={animate} reduce={reduce} />
            {/* Invisible guide the moon follows; the visible halo line draws itself in. */}
            <path ref={haloRef} d={HALO_PATH} fill="none" stroke="none" />
            <motion.path
              d={HALO_PATH}
              className="lp-ring lp-ring-halo"
              initial={reduce ? false : { pathLength: 0, opacity: 0 }}
              animate={entered || reduce ? { pathLength: 1, opacity: 1 } : undefined}
              transition={{ duration: 1.4, ease: [0.4, 0, 0.2, 1], delay: reduce ? 0 : AWAKEN.halo }}
            />
            <motion.path
              d={HALO_PATH}
              className="lp-ring lp-ring-trail"
              style={{ strokeDasharray: trailDash, strokeDashoffset: trailOffset, opacity: trailOpacity }}
            />
          </svg>
          <motion.span
            className="lp-moon left-0 top-0 -ml-[4.5px] -mt-[4.5px]"
            style={{ x: moonX, y: moonY, opacity: moonOpacity }}
          />
        </div>
        {/* Thought sparks. */}
        {SPARKS.map((s) => (
          <motion.span
            key={s.x}
            className="pointer-events-none absolute top-[34%] h-1 w-1 rounded-full bg-white shadow-[0_0_8px_2px_rgba(190,170,255,0.8)]"
            style={{ left: `${s.x}%` }}
            initial={{ opacity: 0 }}
            animate={animate && orbiting ? { y: [0, -80], opacity: [0, 0.9, 0], scale: [0.6, 1, 0.4] } : { opacity: 0 }}
            transition={
              animate && orbiting
                ? { duration: 6, delay: s.delay, repeat: Infinity, repeatDelay: 1.2, ease: "easeOut" }
                : { duration: 0.4 }
            }
          />
        ))}
      </div>

      {/* Outcome pill: its own depth for the pointer, the V6 scroll drift and a gentle float.
          It springs in after the awakening, then cycles: the outgoing line blurs and lifts
          away, the next rises into focus, and the pill resizes smoothly to fit. */}
      <motion.div
        className="absolute bottom-4 left-3 right-3 z-[5] flex justify-center sm:left-auto sm:right-3 sm:bottom-6 lg:-right-10 lg:bottom-[60px]"
        style={{ x: badgeX, y: badgeY }}
      >
        <div className="lp-pv" style={{ "--s": 30 } as CSSProperties}>
          <motion.div
            animate={animate && entered ? { y: [0, -5, 0] } : { y: 0 }}
            transition={animate && entered ? { duration: 6, repeat: Infinity, ease: "easeInOut", delay: AWAKEN.settled + 0.8 } : { duration: 0.4 }}
          >
            <motion.div
              layout
              initial={reduce ? false : { opacity: 0, y: 18, scale: 0.95 }}
              animate={entered || reduce ? { opacity: 1, y: 0, scale: 1 } : undefined}
              transition={{
                default: { type: "spring", stiffness: 140, damping: 18, delay: reduce ? 0 : AWAKEN.settled - 0.5 },
                layout: { duration: 0.55, ease: [0.2, 0.8, 0.2, 1] },
              }}
              className="flex max-w-[400px] items-center gap-3 overflow-hidden rounded-[22px] bg-white/[0.94] px-3.5 py-3 text-[14px] text-(--lp-ink) shadow-[0_20px_40px_-20px_rgba(47,41,37,0.4)] sm:px-4 sm:py-3.5 sm:text-[15px]"
              style={{ borderRadius: 22 }}
            >
              <AnimatePresence mode="wait" initial={false}>
                {(() => {
                  const o = MIND_OUTCOMES[outcome];
                  const Icon = OUTCOME_ICONS[outcome];
                  return (
                    <motion.span
                      key={outcome}
                      layout="position"
                      className="flex items-center gap-3"
                      initial={{ opacity: 0, y: 10, filter: "blur(6px)" }}
                      animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                      exit={{ opacity: 0, y: -10, filter: "blur(6px)" }}
                      transition={{ duration: 0.45, ease: [0.2, 0.8, 0.2, 1] }}
                    >
                      <span className={`lp-tic lp-tone-${o.tone}`} style={{ width: 36, height: 36 }} aria-hidden>
                        <Icon className="h-[18px] w-[18px]" strokeWidth={1.8} />
                      </span>
                      <span className="text-balance pr-1 leading-snug">{o.text}</span>
                    </motion.span>
                  );
                })()}
              </AnimatePresence>
            </motion.div>
          </motion.div>
        </div>
      </motion.div>
    </motion.div>
  );
}
